import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { add, format, parseISO } from "date-fns";
import { useUndoRedo } from "./useUndoRedo";
import { useSelection } from "./useSelection";
import { useAppContext } from "../contexts/AppContext";
import { useBasePath } from "../contexts/BasePathContext";
import { useFileListProjection } from "./useFileListProjection";
import { useFiles } from "./useFiles";
import { toast } from "../components/ui";

// Workspace views where app keyboard shortcuts are active.
const CYCLABLE_VIEWS = ["table", "editor", "calendar", "timelineV3"];

// All views including settings (for routing/mode detection)
const ALL_VIEWS = [...CYCLABLE_VIEWS, "preferences"];

// Helper to derive viewMode from a stripped pathname (no basePath prefix)
const getPathViewMode = (strippedPathname) => {
  if (strippedPathname.startsWith("/settings")) return "preferences";
  if (strippedPathname === "/table") return "table";
  if (strippedPathname === "/calendar") return "calendar";
  if (strippedPathname === "/timeline-v3") return "timelineV3";
  return "editor";
};

const getViewPath = (viewMode) =>
  viewMode === "timelineV3" ? "/timeline-v3" : "/" + viewMode;

const DATE_ADJUSTMENT_TOAST_ID = "date-adjustment-shortcut";
const DATE_ADJUSTMENT_TOAST_DURATION_MS = 1800;

const DATE_ADJUSTMENT_LABELS = {
  minutes: { singular: "minute", plural: "minutes" },
  hours: { singular: "hour", plural: "hours" },
  days: { singular: "day", plural: "days" },
};

function getDateAdjustmentDescription(unit, delta) {
  if (!delta) return "Date Taken unchanged";

  const labels = DATE_ADJUSTMENT_LABELS[unit] || {
    singular: unit,
    plural: unit,
  };
  const amount = Math.abs(delta);
  const label = amount === 1 ? labels.singular : labels.plural;
  const direction = delta < 0 ? "back" : "forward";
  return `Moved ${direction} ${amount} ${label}`;
}

function formatDateAdjustmentTarget(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  return format(date, "EEE, MMM d, yyyy h:mm a");
}

function buildDateAdjustmentToastMessage(description, targetCount, targetDate) {
  const formattedTargetDate = formatDateAdjustmentTarget(targetDate);
  if (targetCount === 1) {
    return formattedTargetDate
      ? `${description}: ${formattedTargetDate}`
      : description;
  }

  return `${description} for ${targetCount} photos.`;
}

export function useKeyboardShortcuts({
  onOpenPresetModal,
  onOpenLocationModal,
  onOpenKeyboardShortcuts,
  onOpenSetDateTime,
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { appPath, stripBase } = useBasePath();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const { clearSelection, selectSingle, primaryIndex } = useSelection();
  const { assignFilesDate, assignFilesDatesIndividually } = useFiles();
  const {
    files,
    selectedIndices,
    selectedCellRange,
    tableVisibleRowMap,
    fileListSort,
    fileListFilters,
    setSelectedIndices,
    setPrimaryIndex,
  } = useAppContext();
  const { visibleIndices } = useFileListProjection({
    files,
    sort: fileListSort,
    filters: fileListFilters,
  });
  const anchorIndexRef = useRef(null);
  const dateAdjustmentToastRef = useRef({
    unit: null,
    delta: 0,
    direction: 0,
    targetCount: 0,
    lastUpdatedAt: 0,
    resetTimer: null,
  });
  const viewMode = getPathViewMode(stripBase(location.pathname));

  useEffect(() => {
    const getTargetIndices = () => {
      if (selectedCellRange) {
        const indices = [];
        for (
          let row = selectedCellRange.minRow;
          row <= selectedCellRange.maxRow;
          row += 1
        ) {
          const mappedIndex = tableVisibleRowMap[row];
          if (Number.isInteger(mappedIndex)) {
            indices.push(mappedIndex);
          } else if (row >= 0 && row < files.length) {
            indices.push(row);
          }
        }
        return Array.from(new Set(indices));
      }

      if (selectedIndices.size > 0) {
        return Array.from(selectedIndices);
      }

      return Number.isInteger(primaryIndex) && primaryIndex >= 0
        ? [primaryIndex]
        : [];
    };

    const openSetDateTime = () => {
      if (getTargetIndices().length === 0) {
        toast.info("Select a photo before setting Date Taken.");
        return;
      }

      if (onOpenSetDateTime) {
        onOpenSetDateTime();
      }
    };

    const showDateAdjustmentToast = (unit, delta, targetCount, targetDate) => {
      const toastState = dateAdjustmentToastRef.current;
      const now = Date.now();
      const direction = Math.sign(delta);
      const isDirectionChange =
        toastState.unit === unit &&
        toastState.direction !== 0 &&
        toastState.direction !== direction;
      if (
        toastState.unit !== unit ||
        toastState.targetCount !== targetCount ||
        isDirectionChange ||
        now - toastState.lastUpdatedAt > DATE_ADJUSTMENT_TOAST_DURATION_MS
      ) {
        toastState.unit = unit;
        toastState.delta = 0;
        toastState.direction = direction;
        toastState.targetCount = targetCount;
      }

      toastState.delta += delta;
      toastState.direction = direction;
      toastState.lastUpdatedAt = now;

      if (toastState.resetTimer) {
        window.clearTimeout(toastState.resetTimer);
      }

      const description = getDateAdjustmentDescription(unit, toastState.delta);
      toast.success(
        buildDateAdjustmentToastMessage(description, targetCount, targetDate),
        {
          id: DATE_ADJUSTMENT_TOAST_ID,
          duration: DATE_ADJUSTMENT_TOAST_DURATION_MS,
        },
      );

      toastState.resetTimer = window.setTimeout(() => {
        toastState.unit = null;
        toastState.delta = 0;
        toastState.direction = 0;
        toastState.targetCount = 0;
        toastState.lastUpdatedAt = 0;
        toastState.resetTimer = null;
      }, DATE_ADJUSTMENT_TOAST_DURATION_MS);
    };

    const shiftTargetDates = (unit, delta) => {
      const targetIndices = getTargetIndices();
      if (targetIndices.length === 0) return;

      const updates = [];
      const hasMissingDate = targetIndices.some((index) => {
        const dateTaken = files[index]?.modifiedExifData?.dateTaken;
        if (!dateTaken) return true;

        const parsed = parseISO(dateTaken);
        if (Number.isNaN(parsed.getTime())) return true;

        updates.push({ index, date: add(parsed, { [unit]: delta }) });
        return false;
      });

      if (hasMissingDate) {
        toast.info("Set an initial date/time before using time adjustment shortcuts.");
        openSetDateTime();
        return;
      }

      assignFilesDatesIndividually(updates);
      showDateAdjustmentToast(
        unit,
        delta,
        targetIndices.length,
        targetIndices.length === 1 ? updates[0]?.date : null,
      );
    };

    const setTargetsToNow = () => {
      const targetIndices = getTargetIndices();
      if (targetIndices.length === 0) return;

      const now = new Date();
      assignFilesDate(targetIndices, now);
      toast.success(
        buildDateAdjustmentToastMessage(
          "Date Taken set to now",
          targetIndices.length,
          now,
        ),
      );
    };

    const handleKeyDown = (e) => {
      // Check if we're in an input field or interactive element that uses arrows/keys
      const target = e.target;
      const isInInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable ||
        target.closest('[role="menu"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="menuitem"]') ||
        target.closest('[role="option"]') ||
        target.closest('[role="combobox"]');

      // Settings has no keyboard shortcuts for file operations.
      if (!CYCLABLE_VIEWS.includes(viewMode)) return;

      // Cmd/Ctrl + Z: Undo
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          const description = undo();
          if (description) {
            console.log("Undone:", description);
          }
        }
      }

      // Cmd/Ctrl + Shift + Z: Redo
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        if (canRedo) {
          e.preventDefault();
          const description = redo();
          if (description) {
            console.log("Redo:", description);
          }
        }
      }

      // Cmd/Ctrl + A: Select All (only when not in input)
      if ((e.metaKey || e.ctrlKey) && e.key === "a" && !isInInput) {
        e.preventDefault();
        anchorIndexRef.current = null;

        if (visibleIndices.length === 0) {
          clearSelection();
          return;
        }

        setSelectedIndices(new Set(visibleIndices));
        if (!visibleIndices.includes(primaryIndex)) {
          setPrimaryIndex(visibleIndices[0]);
        }
      }

      // Escape: Clear Selection (only when not in input)
      if (e.key === "Escape" && !isInInput) {
        anchorIndexRef.current = null;
        clearSelection();
      }

      // ?: Open keyboard shortcuts modal (only when not in input/interactive fields)
      if (
        (e.key === "?" || (e.code === "Slash" && e.shiftKey)) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isInInput
      ) {
        if (onOpenKeyboardShortcuts) {
          e.preventDefault();
          e.stopPropagation();
          onOpenKeyboardShortcuts();
        }
      }

      // J: Set date/time for the current target photos.
      if (
        (e.code === "KeyJ" || e.key === "j" || e.key === "J") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        !isInInput
      ) {
        e.preventDefault();
        e.stopPropagation();
        openSetDateTime();
        return;
      }

      // N: Set date/time to now for the current target photos.
      if (
        (e.code === "KeyN" || e.key === "n" || e.key === "N") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        !isInInput
      ) {
        e.preventDefault();
        e.stopPropagation();
        setTargetsToNow();
        return;
      }

      // Time adjustment shortcuts use brackets so arrow keys stay scoped to
      // the active workspace surface.
      if (
        (
          e.key === "[" ||
          e.key === "]" ||
          e.code === "BracketLeft" ||
          e.code === "BracketRight"
        ) &&
        !isInInput
      ) {
        const direction = e.code === "BracketLeft" || e.key === "[" ? -1 : 1;

        if (!e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          shiftTargetDates("minutes", direction);
          return;
        }

        if (e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          shiftTargetDates("hours", direction);
          return;
        }

        if (e.ctrlKey && e.shiftKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          shiftTargetDates("days", direction);
          return;
        }
      }

      // Shift + Cmd/Ctrl + P: Open preset modal
      if (
        (e.code === "KeyP" || e.key === "p" || e.key === "P") &&
        e.shiftKey &&
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !isInInput
      ) {
        if (onOpenPresetModal) {
          e.preventDefault();
          e.stopPropagation();
          onOpenPresetModal();
        }
      }

      // Shift + Cmd/Ctrl + L: Open location picker modal
      // Shift + Cmd/Ctrl + G: Fallback in browsers where Cmd/Ctrl + Shift + L is reserved
      if (
        (
          e.code === "KeyL" ||
          e.key === "l" ||
          e.key === "L" ||
          e.code === "KeyG" ||
          e.key === "g" ||
          e.key === "G"
        ) &&
        e.shiftKey &&
        (e.metaKey || e.ctrlKey) &&
        !e.altKey &&
        !isInInput
      ) {
        if (onOpenLocationModal) {
          e.preventDefault();
          e.stopPropagation();
          onOpenLocationModal();
        }
      }

      // Cycle workspace views:
      // - macOS/iOS: Option + Arrow Left/Right
      // - non-Apple: Alt + Arrow Left/Right
      // (same keyboard modifier event: altKey)
      const isCycleModifierPressed = e.altKey;
      if (
        isCycleModifierPressed &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !isInInput
      ) {
        const currentIndex = CYCLABLE_VIEWS.indexOf(viewMode);
        if (currentIndex !== -1 && e.key === "ArrowLeft") {
          e.preventDefault();
          const nextIndex =
            currentIndex <= 0 ? CYCLABLE_VIEWS.length - 1 : currentIndex - 1;
          navigate(appPath(getViewPath(CYCLABLE_VIEWS[nextIndex])));
        } else if (currentIndex !== -1 && e.key === "ArrowRight") {
          e.preventDefault();
          const nextIndex =
            currentIndex >= CYCLABLE_VIEWS.length - 1 ? 0 : currentIndex + 1;
          navigate(appPath(getViewPath(CYCLABLE_VIEWS[nextIndex])));
        }
      }

      // Arrow Up/Down: Navigate photos where no richer local arrow model exists.
      if (
        viewMode !== "table" &&
        viewMode !== "timelineV3" &&
        (e.key === "ArrowUp" || e.key === "ArrowDown") &&
        !isInInput &&
        visibleIndices.length > 0
      ) {
        e.preventDefault();
        const direction = e.key === "ArrowUp" ? -1 : 1;
        const currentPosition = visibleIndices.indexOf(primaryIndex);
        const nextPosition =
          currentPosition === -1
            ? direction < 0
              ? visibleIndices.length - 1
              : 0
            : Math.max(
                0,
                Math.min(visibleIndices.length - 1, currentPosition + direction),
              );
        const newIndex = visibleIndices[nextPosition];

        if (e.shiftKey && primaryIndex >= 0 && currentPosition !== -1) {
          if (anchorIndexRef.current === null) {
            anchorIndexRef.current = primaryIndex;
          }

          const anchorPosition = visibleIndices.indexOf(anchorIndexRef.current);
          if (anchorPosition === -1) {
            anchorIndexRef.current = primaryIndex;
          }

          const safeAnchorPosition = visibleIndices.indexOf(anchorIndexRef.current);
          if (safeAnchorPosition === -1) {
            setSelectedIndices(new Set([newIndex]));
            setPrimaryIndex(newIndex);
            return;
          }

          const start = Math.min(safeAnchorPosition, nextPosition);
          const end = Math.max(safeAnchorPosition, nextPosition);
          setSelectedIndices(new Set(visibleIndices.slice(start, end + 1)));
          setPrimaryIndex(newIndex);
          return;
        }

        anchorIndexRef.current = null;
        selectSingle(newIndex);
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      const toastState = dateAdjustmentToastRef.current;
      if (toastState.resetTimer) {
        window.clearTimeout(toastState.resetTimer);
        toastState.resetTimer = null;
      }
    };
  }, [
    undo,
    redo,
    canUndo,
    canRedo,
    clearSelection,
    selectSingle,
    assignFilesDate,
    assignFilesDatesIndividually,
    primaryIndex,
    files,
    selectedIndices,
    selectedCellRange,
    tableVisibleRowMap,
    visibleIndices,
    setSelectedIndices,
    setPrimaryIndex,
    onOpenPresetModal,
    onOpenLocationModal,
    onOpenKeyboardShortcuts,
    onOpenSetDateTime,
    navigate,
    location.pathname,
    viewMode,
    appPath,
    stripBase,
  ]);
}
